/**
 * InputSearch Component - Constants
 * =================================
 */

import { getIcon } from '../../icons';

export type InputSearchSize = 'sm' | 'md';

export interface InputSearchSizeStyles {
  box: string;
  input: string;
  icon: string;
}

export interface InputSearchDefaultValues {
  size: InputSearchSize;
}

// Figma especifica un solo tamaño de campo (h-48, icono search/filter 24, clear 16);
// `sm`/`md` se conservan como API — solo varía el tamaño de texto del valor.
export const SIZES: Record<InputSearchSize, InputSearchSizeStyles> = {
  sm: { box: 'ec-px-3 ec-py-2', input: 'ec-text-base', icon: 'ec-w-6 ec-h-6' },
  md: { box: 'ec-px-3 ec-py-2', input: 'ec-text-base', icon: 'ec-w-6 ec-h-6' }
};

export const BASE_CLASSES = {
  // Contenedor vertical del field completo (label + box + help). Solo se
  // usa como raíz cuando hay slots ec-label/ec-help; su presencia no altera
  // el layout visual del box (mismo `wrapper` de siempre lo sigue rigiendo).
  field: 'ec-flex ec-flex-col ec-gap-1',
  // Botón filter FUERA del campo (§3): gap 4px entre el field y el botón.
  wrapper: 'ec-flex ec-items-center ec-gap-1',
  // Label/help calcados de InputText (patrón field §1).
  label: 'ec-text-base ec-text-color-text-secondary',
  help: 'ec-text-caption ec-font-medium ec-text-color-text-secondary',
  box:
    'ec-flex ec-flex-1 ec-min-w-0 ec-items-center ec-h-12 ec-gap-3 ec-bg-color-surface ec-border ec-border-color-border ' +
    'ec-rounded-2xl ec-transition-colors focus-within:ec-border-color-primary ' +
    'focus-within:ec-ring-1 focus-within:ec-ring-color-primary',
  boxDisabled: 'ec-bg-color-bg ec-cursor-not-allowed',
  input:
    'ec-flex-1 ec-w-full ec-min-w-0 ec-bg-transparent ec-outline-none ec-text-color-text ' +
    'placeholder:ec-text-color-text-muted',
  searchIcon: 'ec-text-color-text-muted ec-flex-shrink-0',
  clearIcon: 'ec-w-4 ec-h-4 ec-flex-shrink-0',
  clearBtn:
    'ec-flex-shrink-0 ec-flex ec-items-center ec-justify-center ec-bg-transparent ec-border-0 ec-p-0 ' +
    'ec-text-color-text-muted hover:ec-text-color-text ec-cursor-pointer ec-rounded-full ' +
    'focus-visible:ec-outline-none focus-visible:ec-ring-2 focus-visible:ec-ring-color-primary',
  // Botón filter (§3): fuera del campo, caja accent-subtle + radio 12, fijo en todos los tamaños.
  filterBtn:
    'ec-flex-shrink-0 ec-flex ec-items-center ec-justify-center ec-bg-color-accent-subtle ' +
    'ec-rounded-xl ec-p-2 ec-text-color-text-muted hover:ec-text-color-text ec-cursor-pointer ec-transition-colors ' +
    'focus-visible:ec-outline-none focus-visible:ec-ring-2 focus-visible:ec-ring-color-primary disabled:ec-cursor-not-allowed'
};

/**
 * Iconos SVG predefinidos
 *
 * Las keys públicas (icon="clear", etc.) se mantienen igual — cero breaking.
 * Los valores vienen del registro central (`src/icons`); `filter` no tiene
 * equivalente en Figma y usa su SVG legacy vía `getIcon()`.
 */
export const ICONS = {
  search: getIcon('search') || '',
  clear: getIcon('clear') || '',
  filter: getIcon('filter') || ''
};

export const DEFAULTS: InputSearchDefaultValues = { size: 'md' };
